"use client";

import { c as _c } from "react/compiler-runtime";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { PlusIcon } from "lucide-react";
import Link from "next/link";
import { useMemo } from "react";
import { Accordion, AccordionContent, AccordionItem } from "@/components/ui/accordion";
import { useI18n } from "@llmgateway/i18n/react";
import { DEV_PLAN_PRICES, getDevPlanCreditsLimit } from "@llmgateway/shared";
const faqItems = [{
  id: "usage",
  questionKey: "faq.items.usage.question",
  answerKey: "faq.items.usage.answer"
}, {
  id: "calculation",
  questionKey: "faq.items.calculation.question",
  answerKey: "faq.items.calculation.answer"
}, {
  id: "limit",
  questionKey: "faq.items.limit.question",
  answerKey: "faq.items.limit.answer"
}, {
  id: "changePlans",
  questionKey: "faq.items.changePlans.question",
  answerKey: "faq.items.changePlans.answer"
}, {
  id: "tools",
  questionKey: "faq.items.tools.question",
  answerKey: "faq.items.tools.answer"
}, {
  id: "models",
  questionKey: "faq.items.models.question",
  answerKey: "faq.items.models.answer"
}];
function formatUsd(amount: number): string {
  return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2).replace(/\.?0+$/, "")}`;
}
export function Faq() {
  const $ = _c(42);
  const {
    t
  } = useI18n();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      litePrice: formatUsd(DEV_PLAN_PRICES.lite),
      proPrice: formatUsd(DEV_PLAN_PRICES.pro),
      maxPrice: formatUsd(DEV_PLAN_PRICES.max),
      liteUsage: formatUsd(getDevPlanCreditsLimit("lite")),
      proUsage: formatUsd(getDevPlanCreditsLimit("pro")),
      maxUsage: formatUsd(getDevPlanCreditsLimit("max"))
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const values = t0;
  let t1;
  if ($[1] !== t) {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map(item => ({
        "@type": "Question",
        name: t(item.questionKey),
        acceptedAnswer: {
          "@type": "Answer",
          text: t(item.answerKey, values)
        }
      }))
    };
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = /</g;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    t1 = JSON.stringify(schema).replace(t2, "\\u003c");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const faqSchemaJson = t1;
  let t2;
  if ($[4] !== faqSchemaJson) {
    t2 = <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: faqSchemaJson
    }} />;
    $[4] = faqSchemaJson;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("faq.eyebrow");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t3) {
    t4 = <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-4">{t3}</p>;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t) {
    t5 = t("faq.title");
    $[10] = t;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t5) {
    t6 = <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">{t5}</h2>;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t) {
    t7 = t("faq.description");
    $[14] = t;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = <p className="mt-4 text-muted-foreground">{t7}</p>;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t) {
    t9 = t("faq.contactPrefix");
    $[18] = t;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t) {
    t10 = t("faq.contactUs");
    $[20] = t;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10) {
    t11 = <Link href="mailto:contact@doteb.com" className="text-foreground underline underline-offset-4">{t10}</Link>;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t11 || $[25] !== t9) {
    t12 = <p className="mt-6 text-sm text-muted-foreground">{t9}{" "}{t11}</p>;
    $[24] = t11;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t4 || $[29] !== t6 || $[30] !== t8) {
    t13 = <div className="lg:col-span-2 lg:sticky lg:top-24 lg:self-start">{t4}{t6}{t8}{t12}</div>;
    $[27] = t12;
    $[28] = t4;
    $[29] = t6;
    $[30] = t8;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t) {
    t14 = faqItems.map((item_0, index) => <AccordionItem key={item_0.id} value={`item-${index + 1}`} className="py-5 border-border/50"><AccordionPrimitive.Header className="flex"><AccordionPrimitive.Trigger className="focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-center justify-between gap-4 rounded-md py-2 text-left font-display text-lg md:text-xl font-medium leading-7 transition-all outline-none focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&>svg>path:last-child]:origin-center [&>svg>path:last-child]:transition-all [&>svg>path:last-child]:duration-200 [&[data-state=open]>svg]:rotate-180 [&[data-state=open]>svg>path:last-child]:rotate-90 [&[data-state=open]>svg>path:last-child]:opacity-0 text-foreground">{t(item_0.questionKey)}<PlusIcon size={18} className="pointer-events-none shrink-0 opacity-60 transition-transform duration-200" aria-hidden="true" /></AccordionPrimitive.Trigger></AccordionPrimitive.Header><AccordionContent className="overflow-hidden transition-all data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up text-base text-muted-foreground leading-relaxed pb-2"><div className="border-l-2 border-foreground/10 pl-4">{t(item_0.answerKey, values)}</div></AccordionContent></AccordionItem>);
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t14) {
    t15 = <div className="lg:col-span-3"><Accordion type="single" collapsible={true} className="w-full" defaultValue="item-1">{t14}</Accordion></div>;
    $[34] = t14;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t13 || $[37] !== t15) {
    t16 = <div className="container mx-auto px-4 md:px-6"><div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">{t13}{t15}</div></div>;
    $[36] = t13;
    $[37] = t15;
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== t16 || $[40] !== t2) {
    t17 = <section className="w-full py-20 md:py-32 bg-background" id="faq">{t2}{t16}</section>;
    $[39] = t16;
    $[40] = t2;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}